import { Network, BookOpen, Folder, Home, Building2, type LucideIcon } from "lucide-react";

/**
 * App-wide SEMANTIC icons. Import these (not the raw lucide glyph) wherever a facility, a category or a manual is
 * represented, so the visual language stays consistent everywhere and the glyph can be swapped in ONE place.
 */

/** The universal FACILITY mark — facilities form a hierarchy (ltree), so a node-tree glyph. Use anywhere a facility is shown. */
export const FacilityIcon: LucideIcon = Network;

/** The universal MANUAL mark — an open book (a manual is a step-by-step document you read/author). Use for manual counts + the manuals view. */
export const ManualIcon: LucideIcon = BookOpen;

/** The universal CATEGORY mark — a FacilityCategory groups a facility's manuals (ADR-007), so a folder (the group that holds the manuals). */
export const CategoryIcon: LucideIcon = Folder;

/** A ROOT facility (top of a realm's forest) — a "home" anchor. TREE-LOCAL: only used for per-node tree glyphs. */
export const RootFacilityIcon: LucideIcon = Home;

/** A LEAF facility (no sub-facilities) — a physical place with no sub-areas. TREE-LOCAL: only used for per-node tree glyphs. */
export const LeafFacilityIcon: LucideIcon = Building2;

/**
 * The glyph for ONE node in the facility tree, by its structural role:
 * - **root** (`parentId === null`) → {@link RootFacilityIcon} (Home)
 * - **branch** (has children) → {@link FacilityIcon} (Network — the universal facility mark)
 * - **leaf** (no children) → {@link LeafFacilityIcon} (Building2)
 *
 * TREE-LOCAL by design: the universal {@link FacilityIcon} still marks "the concept of a facility" everywhere else
 * (category headers, the error dialog). This only differentiates rows WITHIN the tree, where the extra role signal
 * aids scanning; `childCount` is the node's true direct-child count (not the filtered view), so the glyph reflects
 * structure, not the current search.
 */
export function facilityNodeIcon(node: { parentId: number | null; childCount: number }): LucideIcon {
  if (node.parentId === null) return RootFacilityIcon;
  return node.childCount > 0 ? FacilityIcon : LeafFacilityIcon;
}
